import { post } from "./api";
import type {
	DetectResponse,
	ISO639_1_Language,
	SupportedLanguage,
} from "@shared/types";
import { trackEvent } from "./metrics";

interface DetectRequestData {
	text: string;
	language: ISO639_1_Language;
}

const convertMap: Partial<Record<ISO639_1_Language, SupportedLanguage>> = {
	es_419: "es",
	zh_cn: "zh-cn",
	zh_tw: "zh-tw",
	pt_br: "pt",
	pt_pt: "pt",
	he: "iw",
	fil: "tl",
};

const getLanguage = (value: ISO639_1_Language): SupportedLanguage => {
	if (convertMap[value]) {
		return convertMap[value];
	}
	return value as SupportedLanguage;
};

export function detect(data: DetectRequestData): Promise<DetectResponse> {
	const normalizedData = {
		...data,
		language: getLanguage(data.language),
	};
	const t0 = performance.now();

	return post("detect", normalizedData)
		.then((response) => {
			const t1 = performance.now();

			trackEvent({
				event: "detect_responded",
				metadata: {
					time: t1 - t0,
					textLength: data.text.length,
					language: data.language,
				},
			});

			return response;
		})
		.catch((error: unknown) => {
			// console.error("Error in detect service:", error);
			throw new ClientDetectError(
				"An error occurred while detecting AI content",
				{ cause: error },
			);
		});
}

export const SCORE_THRESHOLDS = {
	veryLikelyAi: 0.85,
	mostlyAi: 0.65,
	mixed: 0.45,
	mostlyHuman: 0.25,
	veryLikelyHuman: 0,
};

export const scoreColors = {
	veryLikelyAi: "#F5253D",
	mostlyAi: "#f8763d",
	mixed: "#F9C43D",
	mostlyHuman: "#89b644",
	veryLikelyHuman: "#1AAA4D",
};

export const getColorForScore = (score: number) => {
	if (score >= SCORE_THRESHOLDS.veryLikelyAi) return scoreColors.veryLikelyAi;
	if (score >= SCORE_THRESHOLDS.mostlyAi) return scoreColors.mostlyAi;
	if (score >= SCORE_THRESHOLDS.mixed) return scoreColors.mixed;
	if (score >= SCORE_THRESHOLDS.mostlyHuman) return scoreColors.mostlyHuman;
	return scoreColors.veryLikelyHuman;
};

class ClientDetectError extends Error {
	cause: unknown;
	constructor(message: string, cause?: unknown) {
		super(message);
		this.name = "ClientDetectError";
		this.cause = cause;
	}
}
